"use client";

import { CarouselContentVisible, CarouselItem } from "~/components/ui/carousel";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import Heading from "~/componentsDS/Typography/Heading";
import Carousel from "~/componentsDS/Carousel";
import Reveal from "~/componentsDS/Reveal";
import Answer, { AnswerVideoEvents } from "~/componentsDS/Answer";
import ElementorContainer from "~/componentsDS/ElementorLoyout/ElementorContainer";
import Badge from "~/componentsDS/Badge";
import { oestraDoctorGuidanceItems } from "~/components/features/oestra/data/doctorGuidance";
import { useAnswerCarouselState } from "~/hooks/useAnswerCarouselState";
import { cn } from "~/lib/utils";
import type { GtmEvent } from "~/lib/gtm/getGtmEventData";

type Props = {
    sectionClassName?: string;
    showBadgeOnMobile?: boolean;
    videoEvents?: AnswerVideoEvents;
    eventDataOnExpand?: Omit<GtmEvent["value"], "meta_data">;
}

const defaultVideoEvents: AnswerVideoEvents = {
    play: "vsl_play",
    stop: "vsl_stop"
}

const MeetDrSarahSection = ({
    sectionClassName,
    showBadgeOnMobile = false,
    videoEvents = defaultVideoEvents,
    eventDataOnExpand,
}: Props) => {
    const {
        activeIndex,
        carouselRef,
        emblaApi,
        isInteracted,
        isVisible,
        setEmblaApi,
    } = useAnswerCarouselState();

    return (
        <section className={cn("pt-8 md:pt-[96px] md:pb-[50px] overflow-hidden", sectionClassName)}>
            <ElementorContainer innerClassName="flex flex-col lg:flex-row">
                <div className=" shrink-0 bg-pureWhite z-[1] lg:pr-1 lg:shadow-[-400px_0_0_400px_#fff]">
                    <div className="w-[350px] lg:w-[376px]">
                        <Reveal className="flex flex-col">
                            <Badge variant="primary" className={cn(
                                "w-fit font-medium mb-4",
                                !showBadgeOnMobile && "hidden lg:block"
                            )}>
                                Meet Dr. Sarah
                            </Badge>
                            <Heading size="sm" sizes={{ lg: "md" }} className="mb-4 text-primary500 max-lg:max-w-[300px]">
                                Real guidance from the <br />
                                doctor behind Oestra<sup>®</sup>
                            </Heading>
                        </Reveal>

                        <Paragraph className="text-primary500 mb-6" size="lg">Dr. Sarah created Inner Balance to deliver a different kind of hormonal care.</Paragraph>
                        <Paragraph className="text-primary500" size="lg">Her approach centers on bioidentical hormone enrichment, safe delivery methods, and treatment guided by how women actually feel - not just lab numbers.</Paragraph>
                    </div>
                </div>

                <div ref={carouselRef} className="lg:w-1/2 flex-grow relative max-lg:mt-8">
                    <Carousel setApi={setEmblaApi} opts={{ align: "start", containScroll: false }} loop={false}>
                        <CarouselContentVisible className="max-lg:-ml-2">
                            {oestraDoctorGuidanceItems.map((item, idx) => {
                                return (
                                    <CarouselItem key={idx} className="flex shrink-0 h-auto basis-full md:basis-[40%] lg:basis-[420px] max-lg:pl-2" onClick={() => emblaApi?.scrollTo(idx)}>
                                        <Answer
                                            {...item}
                                            isActive={idx === activeIndex}
                                            isInteracted={isInteracted}
                                            isVisible={isVisible}
                                            widget="meet_dr_sarah"
                                            videoEvents={videoEvents}
                                            eventDataOnExpand={eventDataOnExpand}
                                        />
                                    </CarouselItem>
                                );
                            })}
                        </CarouselContentVisible>
                    </Carousel>
                </div>
            </ElementorContainer>
        </section>
    );
};

export default MeetDrSarahSection;
